import { useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  Gift,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UserRound,
  WalletCards,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { getWalletBalance } from "../../../Redux/wallet_apis/slice";
import { useAuth } from "../../../context/AuthContext";
import DashboardCard from "./DashboardCard";
import DashboardListCards from "./DashboardListCards";
import CustDashTestimonials from "./CustDashTestimonials";
import SellDashTestimonials from "./SellDashTestimonials";
import "./UserDashboard.css";
import "./Dashboard.css";

type IncentiveItem = {
  id: string;
  label: string;
  shortLabel: string;
  amount: number;
  color: string;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatCompactCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

const formatDate = (dateString?: string) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const { balance, loading, error } = useAppSelector((state) => state.wallet);
  const { user } = useAuth();

  const [activeIncentive, setActiveIncentive] = useState<string | null>(null);

  useEffect(() => {
    if (user?._id) {
      dispatch(getWalletBalance());
    }
  }, [dispatch, user?._id]);

  const commissions = balance?.commissionBalances;

  const incentiveBreakdown: IncentiveItem[] = useMemo(
    () => [
      {
        id: "flow",
        label: "Salar Flow",
        shortLabel: "Flow",
        amount: commissions?.flowCommission || 0,
        color: "#173c72",
      },
      {
        id: "loyalty",
        label: "Loyalty Referral",
        shortLabel: "Loyalty",
        amount: commissions?.referralCommission || 0,
        color: "#3978c8",
      },
      {
        id: "samriddhi",
        label: "Salar Samriddhi",
        shortLabel: "Samriddhi",
        amount: commissions?.samriddhiCommission || 0,
        color: "#147a6f",
      },
      {
        id: "community",
        label: "Community Growth",
        shortLabel: "Community",
        amount: commissions?.communityGrowthCommission || 0,
        color: "#6e57c8",
      },
      {
        id: "unity",
        label: "Salar Unity",
        shortLabel: "Unity",
        amount: commissions?.unityCommission || 0,
        color: "#1686a7",
      },
      {
        id: "pearls",
        label: "Salar Pearls",
        shortLabel: "Pearls",
        amount: commissions?.pearlsCommission || 0,
        color: "#d96b22",
      },
      {
        id: "milestone",
        label: "Milestone",
        shortLabel: "Milestone",
        amount: commissions?.milestoneReferralCommission || 0,
        color: "#b84d75",
      },
      {
        id: "daily",
        label: "Daily Incentives",
        shortLabel: "Daily",
        amount: commissions?.sponsorCommission || 0,
        color: "#4f6175",
      },
    ],
    [commissions],
  );

  const totalIncentives = useMemo(
    () => incentiveBreakdown.reduce((total, item) => total + item.amount, 0),
    [incentiveBreakdown],
  );

  const activeBenefits = useMemo(
    () => incentiveBreakdown.filter((item) => item.amount > 0).length,
    [incentiveBreakdown],
  );

  const activeItem =
    incentiveBreakdown.find((item) => item.id === activeIncentive) ?? null;

  const activePercentage = activeItem
    ? (activeItem.amount / (totalIncentives || 1)) * 100
    : 100;

  const circumference = 2 * Math.PI * 76;

  let runningOffset = 0;
  const chartSlices = incentiveBreakdown.map((item) => {
    const percentage =
      totalIncentives > 0 ? (item.amount / totalIncentives) * 100 : 0;
    const length = (percentage / 100) * circumference;
    const slice = { ...item, percentage, length, offset: runningOffset };
    runningOffset += length;
    return slice;
  });

  const topSlice =
    chartSlices.reduce<IncentiveItem & { percentage: number }>(
      (best, slice) => (slice.amount > best.amount ? slice : best),
      chartSlices[0] ?? {
        id: "",
        label: "Incentives",
        shortLabel: "Total",
        amount: 0,
        color: "#173c72",
        percentage: 0,
        offset: 0,
        length: 0,
      },
    );

  if (loading || user === null) {
    return (
      <div className="salar-dashboard salar-dashboard-loading d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const displayName = user?.name || user?.fullName || "Salar Member";

  return (
    <section className="user-dashboard-page salar-dashboard">
      <div className="user-dashboard-shell salar-dashboard-container">
        <header className="user-dashboard-topbar">
          <div className="user-dashboard-heading">
            <div className="user-dashboard-heading-icon">
              <Sparkles size={18} />
            </div>
            <div>
              <p className="user-dashboard-overline">USER DASHBOARD</p>
              <h1>
                Good to see you, <span>{displayName}</span>
              </h1>
              <p className="user-dashboard-heading-subtitle">
                Your account, incentives and activity in one clean overview.
              </p>
            </div>
          </div>

          <div className="user-dashboard-member-mini">
            {user?.profilePicture ? (
              <img src={user.profilePicture} alt={displayName} />
            ) : (
              <span
                className="user-dashboard-member-initials"
                aria-label={`${displayName} initials`}
              >
                {getInitials(displayName)}
              </span>
            )}
            <div>
              <span>{displayName}</span>
              <strong>{user?.registerId || "—"}</strong>
            </div>
            <div className="user-dashboard-member-mini-badge">
              <ShieldCheck size={16} />
              Verified
            </div>
          </div>
        </header>

        <section className="user-dashboard-kpis">
          <article className="user-dashboard-kpi user-dashboard-kpi-primary">
            <div className="user-dashboard-kpi-icon">
              <WalletCards size={22} />
            </div>
            <div>
              <span>Available Incentives</span>
              <strong>₹{(balance?.availableBalance || 0).toFixed(2)}</strong>
              <small>Current available balance</small>
            </div>
          </article>

          <article className="user-dashboard-kpi">
            <div className="user-dashboard-kpi-icon">
              <TrendingUp size={22} />
            </div>
            <div>
              <span>Total Earnings</span>
              <strong>
                ₹{(commissions?.totalCommissions || 0).toFixed(2)}
              </strong>
              <small>Lifetime incentive earnings</small>
            </div>
          </article>

          <article className="user-dashboard-kpi">
            <div className="user-dashboard-kpi-icon">
              <Gift size={22} />
            </div>
            <div>
              <span>Active Benefits</span>
              <strong>{activeBenefits}</strong>
              <small>Currently active programs</small>
            </div>
          </article>
        </section>

        <div className="user-dashboard-main-grid">
          <DashboardCard user={user} />

          <article className="user-dashboard-earnings-card">
            <div className="user-dashboard-card-head">
              <div>
                <p className="user-dashboard-overline">EARNINGS ANALYTICS</p>
                <h2>Incentive distribution</h2>
                <p>
                  Select any category to inspect its exact value and
                  contribution.
                </p>
              </div>
              <div className="user-dashboard-total-box">
                <span>Total Incentives</span>
                <strong>{formatCurrency(totalIncentives)}</strong>
              </div>
            </div>

            <div className="user-dashboard-chart-area">
              <div className="user-dashboard-donut-side">
                <div className="user-dashboard-donut-wrap">
                  <svg
                    className="user-dashboard-donut"
                    viewBox="0 0 180 180"
                    role="img"
                    aria-label="Incentive distribution chart"
                  >
                    <circle
                      cx="90"
                      cy="90"
                      r="76"
                      fill="none"
                      stroke="#e8eef6"
                      strokeWidth="20"
                    />
                    {chartSlices.map((slice) => (
                      <circle
                        key={slice.id}
                        cx="90"
                        cy="90"
                        r="76"
                        fill="none"
                        stroke={slice.color}
                        strokeWidth={
                          activeIncentive === null ||
                          activeIncentive === slice.id
                            ? 20
                            : 12
                        }
                        strokeLinecap="round"
                        strokeDasharray={`${Math.max(
                          slice.length - 4.2,
                          0,
                        )} ${circumference}`}
                        strokeDashoffset={-slice.offset}
                        transform="rotate(-90 90 90)"
                        className="user-dashboard-chart-segment"
                        onMouseEnter={() => setActiveIncentive(slice.id)}
                        onMouseLeave={() => setActiveIncentive(null)}
                        onClick={() =>
                          setActiveIncentive((current) =>
                            current === slice.id ? null : slice.id,
                          )
                        }
                      />
                    ))}
                  </svg>

                  <div className="user-dashboard-donut-center">
                    <span>{activeItem ? activeItem.shortLabel : "Total"}</span>
                    <strong>
                      {formatCompactCurrency(
                        activeItem ? activeItem.amount : totalIncentives,
                      )}
                    </strong>
                    <small>
                      {activeItem
                        ? `${activePercentage.toFixed(1)}% share`
                        : "100% distribution"}
                    </small>
                  </div>
                </div>

                <div className="user-dashboard-chart-selection">
                  <span>
                    {activeItem ? "Selected incentive" : "Highest contribution"}
                  </span>
                  <strong>{activeItem ? activeItem.label : topSlice.label}</strong>
                  <small>
                    {activeItem
                      ? `${formatCurrency(activeItem.amount)} · ${activePercentage.toFixed(1)}%`
                      : `${formatCurrency(topSlice.amount)} · ${topSlice.percentage.toFixed(1)}%`}
                  </small>
                </div>
              </div>

              <div className="user-dashboard-legend">
                {chartSlices.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`user-dashboard-legend-row ${
                      activeIncentive === item.id ? "is-active" : ""
                    }`}
                    onMouseEnter={() => setActiveIncentive(item.id)}
                    onMouseLeave={() => setActiveIncentive(null)}
                    onClick={() =>
                      setActiveIncentive((current) =>
                        current === item.id ? null : item.id,
                      )
                    }
                  >
                    <span
                      className="user-dashboard-legend-dot"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="user-dashboard-legend-copy">
                      <span className="user-dashboard-legend-line">
                        <strong>{item.label}</strong>
                        <b>{formatCompactCurrency(item.amount)}</b>
                      </span>
                      <span className="user-dashboard-legend-track">
                        <span
                          style={{
                            width: `${Math.max(item.percentage, 5)}%`,
                            backgroundColor: item.color,
                          }}
                        />
                      </span>
                    </span>
                    <span className="user-dashboard-legend-percent">
                      {item.percentage.toFixed(1)}%
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="user-dashboard-chart-footer">
              <div>
                <Sparkles size={17} />
                <span>Hover or tap an incentive to inspect its share.</span>
              </div>
              <a className="user-dashboard-link-button text-white" href="/SalarPay">
                View incentive
                <ChevronRight size={17} />
              </a>
            </div>
          </article>
        </div>

        <DashboardListCards balance={balance} />

        <section className="user-dashboard-account-strip">
          <div className="user-dashboard-account-title">
            <div className="user-dashboard-account-icon">
              <UserRound size={20} />
            </div>
            <div>
              <span>MEMBERSHIP</span>
              <strong>Account at a glance</strong>
            </div>
          </div>
          <div className="user-dashboard-account-item">
            <span>Member since</span>
            <strong>{formatDate(user?.doj)}</strong>
          </div>
          <div className="user-dashboard-account-item">
            <span>Invited by</span>
            <strong>{user?.inviterName || user?.invitorName || "—"}</strong>
          </div>
          <div className="user-dashboard-account-item">
            <span>Verification</span>
            <strong
              className={
                user?.kycDetails
                  ? "user-dashboard-kyc-approved"
                  : "user-dashboard-kyc-pending"
              }
            >
              {user?.kycDetails ? "Linked" : "Not Linked"}
            </strong>
          </div>
        </section>

     
       

        {error && (
          <span className="visually-hidden" role="status">
            {error}
          </span>
        )}
      </div>
    </section>
  );
};

export default Dashboard;
